/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { ButtonIcon, Typography } from "./..";
import PropTypes from 'prop-types';
import { forwardRef, memo, useContext, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { bgColor, bgTransparent, borderRadius4px, boxBorder, breakWord, cursorPointer, displayFlex, displayInlineBlock, displayInlineFlex, flexRow, flexRowReverse, inset, itemsCenter, justifyBetween, justifyCenter, justifyEnd, mgl, mgr, parseMaxWidthHeight, parseMinHeight, parseMinWidthHeight, parseWidth, parseWidthHeight, pd, pointerEventsInitial, pointerEventsNone, positionAbsolute, positionRelative, textColor, userSelectNone } from "../../styles/general";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";
import AccordionContext from "./context";
const {
  colors: {
    system: {
      active: systemActive,
      disabled: systemDisabled
    }
  },
  typography: {
    heading3
  },
  spacing
} = useTheme();
const AccordionSummary = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  background,
  children,
  className,
  collapseIcon,
  expandIcon,
  expandIconAt,
  expandIconProps,
  id,
  onClick,
  style,
  title
}, reference) => {
  if (!reference) reference = useRef(null);
  const {
    expanded,
    expandState,
    disabled,
    onClickAccordion,
    onExpand,
    onCollapse,
    setExpandState,
    handleTransition,
    fullHeight
  } = useContext(AccordionContext);
  const ref = useRef(null);
  const expandIconRef = useRef(null);
  const timer = useRef(null);
  const _SummaryRootCSS = SummaryRootCSS(background);
  const _onClick = e => {
    if (disabled) return;
    const target = e.target;
    if (onClickAccordion) {
      onClickAccordion();
      return;
    }
    if (onClick && (!expandIconRef.current || !expandIconRef.current.contains(target))) {
      onClick(e);
      return;
    }
    if (expandState) {
      if (expanded === undefined) setExpandState(false);
      !!onCollapse && onCollapse(ref.current);
    } else if (!expandState) {
      if (expanded === undefined) setExpandState(true);
      !!onExpand && onExpand(ref.current);
    }
  };
  const showHideDetailHandler = type => {
    if (!ref.current) return;
    const detailEl = ref.current.nextElementSibling;
    if (detailEl) {
      const summaryHeight = ref.current.clientHeight;
      if (type === 'show') {
        const height = Math.max(detailEl.scrollHeight, fullHeight - summaryHeight);
        handleTransition(timer, detailEl, height, `calc(100% - ${summaryHeight}px)`);
      } else {
        if (detailEl.style.height) {
          const height = detailEl.scrollHeight;
          handleTransition(timer, detailEl, height);
        }
      }
    }
  };
  useEffect(() => {
    if (expandState) {
      showHideDetailHandler('show');
    } else {
      showHideDetailHandler('hide');
    }
  }, [expandState]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    return jsx("div", {
      id: id,
      css: _SummaryRootCSS,
      ref: ref,
      tabIndex: "-1",
      onClick: _onClick,
      style: style,
      className: classNames('DGN-UI-Accordion-Summary', disabled && 'disabled', expandIcon && expandIconAt, expandState && 'expanding', className)
    }, jsx("div", {
      className: 'DGN-UI-Accordion-Summary-Content'
    }, jsx(Typography, {
      type: 'h3',
      lineClamp: 2,
      color: disabled ? 'system/disabled' : 'system/active',
      style: {
        marginRight: children && title ? spacing([4]) : 0
      }
    }, title || ''), children), !!expandIcon && jsx("div", {
      className: 'Accordion-Icon-Root'
    }, jsx("div", {
      ...expandIconProps,
      ref: expandIconRef,
      className: classNames('Accordion-Icon', !collapseIcon && 'rotate-able', expandIconProps === null || expandIconProps === void 0 ? void 0 : expandIconProps.className)
    }, typeof expandIcon === 'string' ? jsx(ButtonIcon, {
      viewType: 'ghost',
      name: expandIcon,
      className: 'Accordion-Expand-Icon'
    }) : jsx("span", {
      className: 'Accordion-Expand-Icon effect'
    }, expandIcon), !!collapseIcon && (typeof collapseIcon === 'string' ? jsx(ButtonIcon, {
      viewType: 'ghost',
      name: collapseIcon,
      className: 'Accordion-Collapse-Icon'
    }) : jsx("span", {
      className: 'Accordion-Collapse-Icon effect'
    }, collapseIcon)))));
  }, [background, children, className, collapseIcon, expandIcon, expandIconAt, expandIconProps, id, onClick, style, title, disabled, expanded, expandState, onClickAccordion, onCollapse, onExpand]);
}));
const SummaryRootCSS = background => css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${justifyBetween};
    ${itemsCenter};
    ${cursorPointer};
    ${boxBorder};
    ${borderRadius4px};
    ${parseWidth('100%')};
    ${parseMinHeight(56)};
    ${pd([4, 6])};
    ${bgColor(background)};
    .Accordion-Expand-Icon,
    .Accordion-Collapse-Icon {
        ${cursorPointer};
        color: ${systemActive} !important;
        &:active,
        &:focus,
        &:hover {
            ${bgColor('transparent')};
            ${textColor('system/active')};
        }
    }
    .Accordion-Icon-Root {
        ${displayFlex};
        ${flexRow};
        .Accordion-Icon.rotate-able {
            ${boxBorder};
            ${parseWidthHeight(24)};
            &:not(.TreeView) {
                transform: rotateZ(0deg);
                transform-origin: center;
            }
            &.TreeView {
                transform-origin: center;
            }
        }
    }
    &.start {
        ${flexRowReverse};
        ${justifyEnd};
        .Accordion-Icon.rotate-able {
            ${mgr([2])};
            &.TreeView {
                transform: rotateZ(-90deg);
            }
        }
    }
    &.end {
        .Accordion-Icon.rotate-able {
            ${mgl([2])};
            &.TreeView {
                transform: rotateZ(90deg);
            }
        }
    }
    &.expanding {
        border-radius: 4px 4px 0px 0px;
        .Accordion-Icon.rotate-able {
            &:not(.TreeView) {
                transform: rotateZ(90deg);
            }
            &.TreeView {
                transform: rotateZ(0deg);
            }
        }
        .Accordion-Icon:not(.rotate-able) {
            .Accordion-Expand-Icon {
                transform: scale(0);
            }
            .Accordion-Collapse-Icon {
                transform: scale(1);
            }
        }
    }
    &:not(.disabled) {
        transition: background-color 300ms ease;
        &:hover {
            ${bgColor('fill.hover')};
        }
        &:focus {
            ${bgColor('fill.focus')};
        }
        &:active {
            ${bgColor('fill.pressed')};
        }
    }
    &.disabled {
        ${pointerEventsNone};
        ${bgColor('fill.disabled')};
        .DGN-UI-Accordion-Summary-Content,
        .Accordion-Expand-Icon {
            color: ${systemDisabled} !important;
        }
    }
    &.treeview-disabled {
        ${pointerEventsNone};
        &:hover {
            ${bgTransparent};
        }
        .TreeView-Item {
            ${textColor('system.disabled')};
        }
        .Accordion-Icon.rotate-able.TreeView .DGN-UI-Icon {
            ${cursorPointer};
            ${pointerEventsInitial};
        }
    }
    .DGN-UI-Accordion-Summary-Content {
        ${heading3};
        ${flexRow};
        ${positionRelative};
        ${breakWord};
        ${userSelectNone};
        ${boxBorder}
        ${itemsCenter};
        ${textColor('system.active')};
        ${parseWidth('100%')};
        ${parseMinHeight(24)};
    }
    .Accordion-Icon {
        ${displayInlineBlock};
        ${positionRelative};
        ${cursorPointer};
        ${parseWidthHeight(24)};
        transition: transform 200ms linear;
        .Accordion-Expand-Icon {
            transform: scale(1);
            transition: transform 200ms linear;
            &.effect {
                ${positionRelative};
            }
        }
        .Accordion-Collapse-Icon {
            ${positionAbsolute};
            ${inset(0)};
            transform: scale(0);
            transition: transform 200ms linear;
        }
        .effect {
            ${displayInlineFlex};
            ${justifyCenter};
            ${itemsCenter};
            ${cursorPointer};
            ${parseMinWidthHeight(24)};
            ${parseMaxWidthHeight(34)};
        }
    }
`;
AccordionSummary.defaultProps = {
  background: 'system/white',
  className: '',
  expandIcon: 'ArrowRight',
  expandIconAt: 'start',
  expandIconProps: {},
  style: {}
};
AccordionSummary.propTypes = {
  /** Background for label summary. */
  background: PropTypes.string,
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** Icon display for collapse icon. */
  collapseIcon: PropTypes.any,
  /** Icon display for expand icon. */
  expandIcon: PropTypes.any,
  /** Position to render icon. */
  expandIconAt: PropTypes.oneOf(['start', 'end']),
  /** Other props of icons. */
  expandIconProps: PropTypes.any,
  /** Callback fired when the component is clicked. */
  onClick: PropTypes.func,
  /** Style inline of component. */
  style: PropTypes.object,
  /** The title of the component. */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};
export default AccordionSummary;